<template>
  <section>
    <LayoutAccount>
      <template #title>
        <h5 v-if="getAppConfigReady && loginSubtitle" v-html="loginSubtitle" class="mb-3 intro-pre-title"></h5>
        {{ $t('pages.signup.headline_title_line1') }} {{ $t('pages.signup.headline_title_line2') }}
      </template>
   <template #mainContent>
      <div class="login-providers" :class="socialPositionClass">

          <!-- Main Forms -->
          <div class="main-form">
            <!-- JWT Auto-Login (highest priority - detected from ?jwt=xxx) -->
            <div v-if="isProviderEnabled('jwt')" class="provider-section">
              <JwtAuthForm @success="handleSignupSuccess" @error="handleAuthError" />
            </div>

            <!-- SSO Auto-Login (detected from ?t=xxx&k=yyy) -->
            <div v-else-if="isProviderEnabled('sso')" class="provider-section">
              <SsoAuthForm @success="handleSignupSuccess" @error="handleAuthError" />
            </div>

            <!-- Email Token Passwordless (priority) -->
            <div v-else-if="isProviderEnabled('email_token')" class="provider-section">
              <EmailTokenUnifiedForm @success="handleSignupSuccess" />
            </div>

            <!-- OR Divider (se ci sono altri provider dopo email token) -->
            <div v-if="isProviderEnabled('email_token') && hasOtherProviders" class="divider-or">
              <hr>
            </div>

            <!-- Password Signup -->
            <div v-if="isProviderEnabled('password')" class="provider-section">
              <PasswordSignupForm @success="handleSignupSuccess" />
            </div>
          </div>

          <!-- Social Login Buttons -->
          <div class="social-buttons">
            <SocialAuthButtons
              :title-key="socialPosition === 'top' ? 'forms.login_form.social_login_title_top' : undefined"
              :apple-disabled="appleDisabled"
              @success="handleSignupSuccess"
              @error="handleSocialError"
            />
          </div>

          <!-- Powered By (per specifici subdomain) -->
          <div class="row d-lg-flex align-items-center mt-4" v-if="showPoweredBy">
            <div class="col-lg-12">
              <PoweredBy :url="poweredByUrl" />
            </div>
          </div>
        </div>
      </template>
    </LayoutAccount>

    <template v-if="isPicaThirdLevelDomain">
      <B2BSection />
    </template>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import LayoutAccount from '@/components/LayoutAccount'
import B2BSection from '@/components/B2BSection.vue'
import PoweredBy from '@/components/PoweredBy.vue'

// Provider components
import EmailTokenUnifiedForm from '@/domain/auth/components/providers/EmailTokenUnifiedForm.vue'
import PasswordSignupForm from '@/domain/auth/components/providers/PasswordSignupForm.vue'
import SocialAuthButtons from '@/domain/auth/components/SocialAuthButtons.vue'
import JwtAuthForm from '@/domain/auth/components/providers/JwtAuthForm.vue'
import SsoAuthForm from '@/domain/auth/components/providers/SsoAuthForm.vue'

export default {
  name: 'Signup',

  components: {
    LayoutAccount,
    B2BSection,
    PoweredBy,
    EmailTokenUnifiedForm,
    PasswordSignupForm,
    SocialAuthButtons,
    JwtAuthForm,
    SsoAuthForm,
  },

  data() {
    return {
      socialError: false
    }
  },

  computed: {
    ...mapGetters(['isPicaThirdLevelDomain', 'thirdLevelDomain', 'disableSocialLoginInPreview', 'showMarketingCheck']),
    ...mapGetters('appConfig', ['getAppConfigReady', 'loginSubtitle']),
    ...mapGetters('auth', [
      'enabledAuthProviders',
      'isAuthProviderEnabled',
      'enabledAuthProvidersWithQuery',
      'hasSocialLoginEnabled'
    ]),

    currentEnabledProviders() {
      return this.enabledAuthProvidersWithQuery(this.$route.query)
    },

    appleDisabled() {
      return this.disableSocialLoginInPreview
    },

    hasSocialProviders() {
      return this.isProviderEnabled('google') ||
             this.isProviderEnabled('facebook') ||
             (this.isProviderEnabled('apple') && !this.appleDisabled)
    },

    hasOtherProviders() {
      return this.isProviderEnabled('password') || this.hasSocialProviders
    },

    showPoweredBy() {
      const domains = ['hublot']
      return domains.includes(this.thirdLevelDomain)
    },

    poweredByUrl() {
      return `https://business.getpica.com/?utm_source=${this.thirdLevelDomain}.getpica.com&utm_medium=website&utm_campaign=${this.thirdLevelDomain}`
    },

    socialPosition() {
      if (['photos'].includes(this.thirdLevelDomain) ) return 'top'
      return 'bottom'
    },

    socialPositionClass() {
      return `social-position-${this.socialPosition}`
    },

    appleDisabled() {
      return this.disableSocialLoginInPreview
    },
  },

  methods: {
    isProviderEnabled(provider) {
      return this.currentEnabledProviders.includes(provider)
    },

    async handleSignupSuccess() {
      // Redirect to checkins list
      await this.$router.push({ name: 'checkins-list' })

      // Show marketing consent modal if needed
      if (this.showMarketingCheck) {
        this.$bvModal.show('modal-marketing-consent')
      }
    },

    handleSocialError(error) {
      console.error('Social signup error:', error)
      this.socialError = true
      setTimeout(() => {
        this.socialError = false
      }, 5000)
    },

    handleAuthError(error) {
      console.error('Authentication error:', error)
      // On JWT/SSO auth failure, fallback to standard forms
    }
  }
}
</script>


